<template>
  <div v-if="options.length === 1 && readOnlySingleOption" class="single-option" :style="{ width: `${width}px` }">
    {{ options[0].name }}
  </div>
  <div v-else>
    <ts-dropdown
      :placeholder="placeholderOrValue"
      :size="size"
      :width="width"
      :popover-width="popoverWidth"
      :open="open"
      :disabled="disabled"
      @click="handleDropdownClick"
      @close="handleSaveClick"
    >
      <template #header>
        <div class="filter" v-if="textFilter">
          <Magnifier class="magnifier" />
          <input type="text" placeholder="Search by name" v-model="filter" />
        </div>
        <slot name="header" v-bind="{ selectOptionHandler: handleSelectedOptionInput, selectable: canSelect }"></slot>
      </template>
      <div class="filter parent" v-if="parentFilter">
        <div v-if="showParentFilter">
          <div class="parents-title">
            {{ parentsTitle }}
          </div>
          <div class="option" v-for="parent of parents" :key="parent.id">
            <ts-radio :label="parent.id" v-model="selectedParent">{{ parent.name }}</ts-radio>
          </div>
          <div class="children-title">
            {{ childrenTitle }}
          </div>
        </div>
      </div>
      <div v-if="!showParentFilter && emptyOptionsText">
        <div class="empty-placeholder">
          {{ emptyOptionsText }}
        </div>
      </div>
      <RecycleScroller
        v-if="options.length > 1000"
        class="scroller"
        :items="filteredOptions"
        :item-size="32"
        key-field="id"
        v-slot="{ item }"
      >
        <div class="option">
          <MultiSelectionDropdownItem
            :item="item"
            :show-picture="showPicture"
            :disabled="isDisabled(item.id)"
            @input="handleSelectedOptionInput(item.id, $event)"
          />
        </div>
      </RecycleScroller>
      <template v-if="grouped">
        <div class="" v-for="parent in filteredOptions" :key="parent.id" style="margin-bottom:1rem;">
                <span>{{ parent.name }}</span>
          <div v-for="option in parent.children" :key="option.id" class="option">
            <ts-checkbox
              :value="checked[option.id]"
              :disabled="isDisabled(option.id)"
              @input="handleSelectedOptionInput(option.id, $event)"
            >
              <div class="option-content">
                <Thumbnail
                  v-if="showPicture && option.pictureUrl"
                  class="picture"
                  size="x-small"
                  format="circle"
                  :url="option.pictureUrl"
                />
                <span>{{ option.name }}</span>
              </div>
            </ts-checkbox>
          </div>
        </div>
      </template>
      <template v-else>
      <div v-for="option in filteredOptions" :key="option.id" class="option">
        <ts-checkbox
          :value="checked[option.id]"
          :disabled="isDisabled(option.id)"
          @input="handleSelectedOptionInput(option.id, $event)"
        >
          <div class="option-content">
            <Thumbnail
              v-if="showPicture && option.pictureUrl"
              class="picture"
              size="x-small"
              format="circle"
              :url="option.pictureUrl"
            />
            <span>{{ option.name }}</span>
          </div>
        </ts-checkbox>
      </div>
      </template>
      <template #footer>
        <div class="actions">
          <ts-link @click="handleResetClick">Reset</ts-link>
          <ts-checkbox v-if="selectAll" :value="allFilteredOptionsAreSelected" @input="handleSelectAllInput">
            Select all
          </ts-checkbox>
          <ts-link type="primary" @click="handleSaveClick">Save</ts-link>
        </div>
      </template>
    </ts-dropdown>
  </div>
</template>

<style lang="scss" scoped>
.filter {
  display: flex;
  padding: 12px 0 0 12px;
  align-items: center;

  &.parent {
    flex-direction: column;
    align-items: flex-start;
    padding: 0;

    .option {
      margin: 4px 8px;
    }

    .parents-title,
    .children-title {
      font-family: Roboto;
      font-size: 12px;
      color: #242424;
      margin: 8px;
    }
  }

  .magnifier {
    width: 20px;
  }

  input {
    font-family: Roboto;
    font-size: 12px;
    line-height: 18px;
    color: #242424;

    width: 100%;
    padding: 8px;
    outline: none;

    &::placeholder {
      color: #9aa5b7;
    }
  }
}

.scroller {
  height: 300px;
}

.option {
  margin: 8px;

  .option-content {
    display: flex;
    align-items: center;
  }

  .picture {
    margin-right: 8px;
  }
}

.actions {
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 12px;
}

.empty-placeholder {
  padding: 10px;
  display: flex;
  justify-content: center;
  font-family: Roboto;
  font-size: 12px;
  font-style: italic;
  color: #6d7587;
}

.single-option {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.32);
  box-shadow: -5px -5px 10px 0 rgba(250, 251, 255, 0.8), 5px 5px 10px 0 rgba(166, 171, 189, 0.48);
  border-radius: 47px;

  white-space: nowrap;
  overflow: hidden;

  font-family: Roboto;
  font-size: 12px;
  color: #6d7587;

  display: flex;
  align-items: center;
  padding: 0 12px;
}
</style>

<script>
import Thumbnail from '@/components/Thumbnail.vue';

import Magnifier from '@/assets/magnifier.svg';

import MultiSelectionDropdownItem from './MultiSelectionDropdownItem.vue';

export default {
  components: {
    Thumbnail,
    Magnifier,
    MultiSelectionDropdownItem
  },
  props: {
    placeholder: {
      type: String,
      default: ''
    },
    grouped: {
      type: Boolean,
      default: false
    },
    textFilter: {
      type: Boolean,
      default: false
    },
    parentFilter: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'small'
    },
    width: {
      type: Number,
      default: 300
    },
    popoverWidth: {
      type: Number,
      default: 0
    },
    options: {
      type: Array,
      default: () => []
    },
    parents: {
      type: Array,
      default: () => []
    },
    parentsTitle: {
      type: String,
      default: null
    },
    emptyOptionsText: {
      type: String,
      default: null
    },
    childrenTitle: {
      type: String,
      default: null
    },
    value: {
      type: Array,
      default: () => []
    },
    maxItems: {
      type: Number,
      default: 0
    },
    showPicture: {
      type: Boolean,
      default: false
    },
    readOnlySingleOption: {
      type: Boolean,
      default: true
    },
    selectAll: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      checked: {},
      open: false,
      filter: '',
      selectedParent: null
    };
  },
  computed: {
    showParentFilter() {
      return this.parentFilter && this.options.length > 0;
    },
    canSelect() {
      return this.maxItems == 0 || Object.values(this.checked).filter(i => i).length < this.maxItems;
    },
    filteredOptions() {
      if (this.textFilter) {
        if (!this.filter || !this.filter.trim().length) {
          return this.options;
        }
        return !this.grouped
        ? this.options.filter(o => o.name.toLowerCase().includes(this.filter.toLowerCase()))
        : this.options.map(parent => {
          return {
            ...parent,
            children: parent.children.filter(o => o.name.toLowerCase().includes(this.filter.toLowerCase()))
          }
        }).filter(p => p.children)
      } else if (this.parentFilter) {
        if (this.selectedParent === null) {
          return this.options;
        }
        return this.options.filter(o => o.parent === this.selectedParent);
      } else {
        return this.options;
      }
    },
    placeholderOrValue() {
      if (!this.value || !this.value.length) {
        return this.placeholder;
      }
      let text = this.flatten().find(o => o.id === this.value[0])?.name || '';
      if (!text) {
        console.error('Multi selection value not found', this.value[0], this.flatten());
      }
      if (this.value.length > 1) {
        text += ` (+${this.value.length - 1})`;
      }
      return text;
    },
    allFilteredOptionsAreSelected() {
      return this.filteredOptions.every(option => {
        return this.grouped
        ? option.children.every(o => this.checked[o.id])
        : this.checked[option.id]
      });
    }
  },
  methods: {
    handleDropdownClick() {
      Object.keys(this.checked).forEach(key => (this.checked[key] = false));
      this.value.forEach(v => (this.checked[v] = true));
      this.selectedParent = null;
      this.open = !this.open;
      this.filter = '';
    },
    isDisabled(optionId) {
      const checkedCount = Object.values(this.checked).filter(v => v).length;
      if (!this.maxItems || checkedCount < this.maxItems) {
        return false;
      } else {
        return !this.checked[optionId];
      }
    },
    handleSelectedOptionInput(id, value) {
      this.checked = {
        ...this.checked,
        [id]: value
      };
      this.$emit(
        'selected',
        Object.keys(this.checked).filter(t => this.checked[t])
      );
    },
    flatten() {
      return this.grouped ? this.options.reduce((prev, curr) => [].concat(prev, curr.children || []), []) : this.options;
    },
    handleSelectAllInput(value) {
      if(! this.grouped) {
        this.filteredOptions.forEach(option => {
          this.handleSelectedOptionInput(option.id, value);
        });
      }
      this.filteredOptions.reduce((prev, curr) => [].concat(prev, curr.children), [])
      .forEach(option => {
        this.handleSelectedOptionInput(option.id, value);
      });
    },
    handleResetClick() {
      this.$emit('reset');
      this.open = false;
      this.$emit('input', []);
    },
    handleSaveClick() {
      this.open = false;
      const checked = this.flatten().filter(o => this.checked[o.id]).map(o => o.id);
      this.$emit('input', checked);
      this.$emit('saved', {
        items: checked,
        selectionHandler: (id, value) => {
          this.handleSelectedOptionInput(id, value);
        }
      });
    }
  }
};
</script>
